import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';

/**
 * Watches for redirects triggered by ProtectedRoute when a user without
 * platform_super_admin privileges tries to hit a /superadmin/* route.
 * Shows an "Access denied" toast and clears the marker so it doesn't repeat.
 */
export default function AccessDeniedWatcher() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const state = location.state as { accessDenied?: boolean; attempted?: string } | null;
    if (state?.accessDenied) {
      // Suppress the destructive toast when the deny came from a post-login
      // redirect chain — Login.tsx already clamps ?redirect= to the user's
      // role zone, and surfacing "Access denied" right after a successful
      // sign-in is what users mistakenly read as "login failed".
      const cameFromLogin =
        typeof document !== 'undefined' && document.referrer.includes('/login');
      if (!cameFromLogin) {
        toast({
          title: 'Access denied',
          description: `You don't have permission to access ${state.attempted || 'that page'}.`,
          variant: 'destructive',
        });
      }
      // Clear state so a refresh doesn't re-fire the toast.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  return null;
}
